import {
    BRANDED_EGIFT,
    CARD_LINK,
    CHANGE_BRAND,
    EDIT_SAVED_CARDS,
    PLASTIC_REDEMPTION,
    UNBRANDED_TO_EGIFT,
    UNBRANDED_TO_LINK,
} from '@/src/modules/pg-plus/constants'
import Button from '@/src/common/components/marketing/buttons/Button'
import { useState } from 'react'
import EGiftModalContentForGiftDetails from '@/src/modules/account/received/GiftWalletEGiftModalContent'
import {
    ADDRESS_ENTRY_STEP,
    SELECT_BRAND_STEP,
} from '@/src/modules/pg-plus/redemption/constants'
import { usePGPlusStepAndModalContext } from '@/src/common/contexts/PGPlusModalAndStepContext'
import {
    DISPATCH_HIDE_MODAL,
    DISPATCH_SET_STEP,
} from '@/src/common/reducers/constants'
import CardLinkModalContent from '@/src/modules/pg-plus/redemption/modal-content/CardLinkModalContent'
import usePostDeleteCard from '@/src/card-linking/hooks/usePostDeleteCard'
import { handleSteps } from '@/src/modules/pg-plus/redemption/utilities'
import { ModalButtonsWrapper } from '@/src/common/components/elements/Modals/StyledModalComponents'

interface IModal {
    hasBeenCardLinked: boolean
    refetchGift: Function
}

const PGPlusRedemptionModalContent = ({
    hasBeenCardLinked,
    refetchGift,
}: IModal) => {
    const {
        dispatch,
        giftState,
        isUnbrandedToLink,
        isUnbrandedToEgift,
        setSelection,
    } = usePGPlusStepAndModalContext()
    const { redemptionSelection, modalName, cardUuid, isPgPlus } = giftState
    const [errors, setErrors] = useState<any[]>([])

    const isChangeBrand: boolean = redemptionSelection === CHANGE_BRAND
    const deleteSavedCard = usePostDeleteCard()

    if (redemptionSelection === BRANDED_EGIFT) {
        return (
            <EGiftModalContentForGiftDetails
                errors={errors}
                setErrors={setErrors}
                refetchGift={refetchGift}
            />
        )
    }

    // everything below here requires an additional step
    if (redemptionSelection === PLASTIC_REDEMPTION) {
        return (
            <>
                <p data-testid={'modal-header'} className={'header'}>
                    Get a plastic Visa{' '}
                    <span className={'gradient'}>Gift Card</span>
                </p>
                <p className={'body-text'}>
                    Once your plastic gift card request is submitted, you&#39;ll
                    no longer be able to select another redemption option.
                </p>
                <ModalButtonsWrapper gap={'5px'} marginTop={'none'}>
                    <Button
                        label={'Confirm'}
                        variant={'primary'}
                        width={'standard'}
                        showArrow={true}
                        textTransform={'uppercase'}
                        data-testid={'exchange-button'}
                        onClick={() => {
                            dispatch({
                                type: DISPATCH_SET_STEP,
                                payload: { stepName: ADDRESS_ENTRY_STEP },
                            })
                        }}
                    />
                    <Button
                        label={'Go back'}
                        variant={'primaryOutline'}
                        width={'standard'}
                        showArrow={true}
                        textTransform={'uppercase'}
                        data-testid={'back-button'}
                        onClick={() => {
                            dispatch({
                                type: DISPATCH_HIDE_MODAL,
                            })
                        }}
                    />
                </ModalButtonsWrapper>
            </>
        )
    }

    // if the user chose to link a newly branded pg+, show the card link modal
    if (isUnbrandedToLink && !isPgPlus) {
        return (
            <CardLinkModalContent
                functions={{
                    handleStepChange: () =>
                        handleSteps({
                            selection: CARD_LINK,
                            dispatch,
                            pushToSuccessStep: hasBeenCardLinked,
                        }),
                    setIsActivating: undefined,
                    refetchGift: refetchGift,
                }}
                deleteCard={deleteSavedCard}
                cardToCardUuid={cardUuid}
            />
        )
    }

    // if a user wants to choose a brand, or a pg+ needs a brand, show this
    if (isChangeBrand || isUnbrandedToLink || isUnbrandedToEgift) {
        const selection: string = isUnbrandedToEgift
            ? UNBRANDED_TO_EGIFT
            : isUnbrandedToLink
            ? UNBRANDED_TO_LINK
            : CHANGE_BRAND

        return (
            <>
                <p data-testid={'modal-header'} className={'header'}>
                    Choose a {!isPgPlus && 'different'} brand for your{' '}
                    <span className={'gradient'}>gift</span>
                </p>
                <p className={'body-text'}>
                    Select one of our many national brand partners to use your
                    gift with.
                </p>
                <ModalButtonsWrapper gap={'5px'} marginTop={'none'}>
                    <Button
                        label={'Confirm'}
                        variant={'primary'}
                        width={'standard'}
                        showArrow={true}
                        textTransform={'uppercase'}
                        data-testid={'exchange-button'}
                        onClick={() => {
                            setSelection(selection)
                            dispatch({
                                type: DISPATCH_SET_STEP,
                                payload: { stepName: SELECT_BRAND_STEP },
                            })
                        }}
                    />
                    <Button
                        label={'Go back'}
                        variant={'primaryOutline'}
                        width={'standard'}
                        showArrow={true}
                        onClick={() => {
                            dispatch({
                                type: DISPATCH_HIDE_MODAL,
                            })
                        }}
                        textTransform={'uppercase'}
                        data-testid={'back-button'}
                    />
                </ModalButtonsWrapper>
            </>
        )
    }

    // lets the user add and delete saved debit/credit cards
    if (modalName === EDIT_SAVED_CARDS) {
        return (
            <CardLinkModalContent
                functions={{
                    handleStepChange: undefined,
                    setIsActivating: undefined,
                    refetchGift: refetchGift,
                }}
                initialStep={'REMOVE_OR_ADD_CARDS'}
                deleteCard={deleteSavedCard}
                cardToCardUuid={cardUuid}
                hideGiftDetailsButton={true}
            />
        )
    }

    return <></>
}

export default PGPlusRedemptionModalContent
